/**
 * Keeping a listener in step with a room (HANDOFF §6.8).
 *
 * ## The problem
 *
 * The server says "this track is at 94,312ms, and that was true at
 * 2026-08-27T05:12:44.918Z". By the time the browser reads it, some unknown
 * amount of that sentence is out of date, and the browser's own clock may be
 * minutes wrong without anybody noticing - a laptop resumed from sleep is
 * routinely off by seconds, and a machine with no NTP by far more.
 *
 * Both are solvable, and neither is solvable by trusting `Date.now()`.
 *
 * ## Estimating the offset
 *
 * Every response carries `server_time`. Comparing it to the local clock at the
 * moment of receipt gives an offset that includes half the round trip - the
 * request took some time to arrive, the response some time to come back, and we
 * cannot tell which half was which. Assuming they were equal is the standard
 * simplification and is wrong by a few milliseconds, which is inaudible.
 *
 * What is *not* inaudible is a single bad sample: one request that queued behind
 * a slow one produces an offset seconds out. So samples are kept and the
 * **median** is used, which a handful of outliers cannot move.
 *
 * ## What "in step" actually means
 *
 * Not "identical". Two browsers decoding the same AAC stream will never be
 * sample-accurate, and chasing that would mean seeking constantly - which is
 * audible as a stutter, and far worse than being 200ms behind.
 *
 * So there are two thresholds. Small drift is corrected by nudging the playback
 * *rate* by a fraction of a percent, which nobody can hear and which lets the
 * player catch up over a few seconds. Only a large gap - somebody who tabbed
 * away, or a track change - is corrected with a seek.
 */

/** Beyond this, seek. Below it, nudge the rate. */
const SEEK_THRESHOLD_MS = 1500;

/** Below this, do nothing at all. Chasing it would be the audible thing. */
const IN_STEP_MS = 120;

/** How far the rate may be bent to catch up. 4% is inaudible on music. */
const MAX_RATE_NUDGE = 0.04;

/** Offset samples kept for the median. */
const SAMPLE_LIMIT = 9;

const samples: number[] = [];

/**
 * Fold one observation into the clock estimate.
 *
 * Call with every response that carried a `server_time`. Cheap, and the more
 * often it is called the less any single bad sample matters.
 */
export function observeServerTime(serverTime: string): void {
  const server = parseServerTime(serverTime);

  if (server === null) return;

  samples.push(server - Date.now());

  if (samples.length > SAMPLE_LIMIT) samples.shift();
}

/** How far ahead of us the server's clock is, in milliseconds. */
export function clockOffset(): number {
  if (samples.length === 0) return 0;

  const sorted = [...samples].sort((a, b) => a - b);

  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/**
 * MySQL hands back `2026-08-27 05:12:44.918`, which is UTC with nothing saying
 * so. `Date` would read it as local time and be out by the whole timezone -
 * which on a synced room is the difference between the right track and silence.
 */
function parseServerTime(value: string): number | null {
  const parsed = Date.parse(value.replace(' ', 'T') + (/[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? '' : 'Z'));

  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Where the room is *now*, given what the server said and when it said it.
 *
 * The whole point: `position_ms` was true at `server_time`, so the answer is
 * that plus however long ago that was, measured on a clock we have corrected.
 *
 * A paused room does not advance, which is why `paused` is a parameter rather
 * than something the caller is trusted to remember.
 */
export function positionNow(state: {
  position_ms: number | null;
  server_time: string;
  paused: boolean;
}): number | null {
  if (state.position_ms === null) return null;
  if (state.paused) return state.position_ms;

  const at = parseServerTime(state.server_time);

  if (at === null) return state.position_ms;

  const elapsed = Date.now() + clockOffset() - at;

  // Clamped: a wildly wrong offset should not produce a negative position that
  // a player cannot seek to.
  return Math.max(0, state.position_ms + elapsed);
}

/**
 * What to do about the gap between where the audio is and where the room is.
 *
 * Returned rather than applied, so the decision is testable and the component
 * doing the applying stays a component.
 */
export type Correction =
  | { kind: 'none' }
  | { kind: 'seek'; to: number }
  | { kind: 'rate'; rate: number };

export function correction(actualMs: number, targetMs: number): Correction {
  const drift = targetMs - actualMs;

  if (Math.abs(drift) < IN_STEP_MS) {
    // Close enough, and the rate goes back to normal - otherwise a player that
    // caught up keeps running fast for ever.
    return { kind: 'rate', rate: 1 };
  }

  if (Math.abs(drift) > SEEK_THRESHOLD_MS) {
    return { kind: 'seek', to: targetMs };
  }

  /*
   * Behind means play slightly faster, ahead means slightly slower.
   *
   * Scaled by how far out we are, so a small gap closes gently and a large one
   * closes at the cap. Music at 1.04x is not something anybody notices; a seek
   * every few seconds absolutely is.
   */
  const nudge = Math.max(-MAX_RATE_NUDGE, Math.min(MAX_RATE_NUDGE, drift / SEEK_THRESHOLD_MS * MAX_RATE_NUDGE));

  return { kind: 'rate', rate: 1 + nudge };
}

/** Forget every sample. Called on sign-out, with the rest of the session. */
export function resetClock(): void {
  samples.length = 0;
}
